import { KeyboardEvent, useCallback, useRef } from 'react'
import cx from 'classnames'
import {
  useVerticalDrag,
  useWheelAdjust,
} from '../../hooks/useDragAdjust'
import styles from './Fader.module.css'
import { clamp } from '@pogokid/util'

/* Full drag travel in pixels for min-to-max */
const DRAG_RANGE_PX = 170

const DEFAULT_LEGEND = ['+6', '0', '-12', '-24', '-40', '-60']

function formatDecibels(value: number) {
  return `${value > 0 ? '+' : ''}${value.toFixed(1)} dB`
}

export interface FaderProps {
  /** Label shown under the fader */
  label: string
  /** Current value */
  value: number
  /** Called with the new value when the user adjusts the fader */
  onChange: (value: number) => void
  /** Minimum value */
  min?: number
  /** Maximum value */
  max?: number
  /**
   * Values above this show the clip warning (orange fill).
   * Defaults to 0 for the hot-signal warning above 0 dB.
   */
  clipAbove?: number
  /** Scale markings shown beside the slot, top to bottom */
  legend?: string[]
  /** Formats the value readout. Defaults to decibels, e.g. "-8.0 dB" */
  formatValue?: (value: number) => string
  /** Additional CSS class */
  className?: string
}

/**
 * A vertical fader control styled like a mixing desk channel.
 * Adjust by dragging vertically, scrolling, or with the
 * arrow keys when focused.
 */
export function Fader({
  label,
  value,
  onChange,
  min = -60,
  max = 6,
  clipAbove = 0,
  legend = DEFAULT_LEGEND,
  formatValue = formatDecibels,
  className,
}: FaderProps) {
  const slotRef = useRef<HTMLDivElement>(null)

  const ratio = (value - min) / (max - min)
  /* Keep the thumb inside 10%-90% of the slot's travel */
  const thumbTopPercent = 10 + (1 - ratio) * 80
  const isClipping = value > clipAbove

  const nudge = useCallback(
    (deltaPx: number) => {
      onChange(
        clamp(
          value + (max - min) * (deltaPx / DRAG_RANGE_PX),
          min,
          max
        )
      )
    },
    [onChange, value, min, max]
  )

  const dragHandlers = useVerticalDrag(nudge)
  useWheelAdjust(slotRef, (direction) => nudge(direction * 5))

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    let next: number
    if (event.key === 'ArrowUp' || event.key === 'ArrowRight') {
      next = clamp(value + 1, min, max)
    } else if (
      event.key === 'ArrowDown' ||
      event.key === 'ArrowLeft'
    ) {
      next = clamp(value - 1, min, max)
    } else if (event.key === 'Home') {
      next = min
    } else if (event.key === 'End') {
      next = max
    } else {
      return
    }
    event.preventDefault()
    onChange(next)
  }

  return (
    <div
      className={cx(styles.Fader, className, {
        [styles.isClipping]: isClipping,
      })}
    >
      <div className={styles.Fader__stage}>
        <div className={styles.Fader__legend} aria-hidden="true">
          {legend.map((mark) => (
            <span key={mark}>{mark}</span>
          ))}
        </div>
        <div
          ref={slotRef}
          className={styles.Fader__slot}
          role="slider"
          tabIndex={0}
          aria-label={label}
          aria-valuemin={min}
          aria-valuemax={max}
          aria-valuenow={Number(value.toFixed(1))}
          aria-valuetext={formatValue(value)}
          onKeyDown={onKeyDown}
          {...dragHandlers}
        >
          <div className={styles.Fader__groove}>
            <div
              className={styles.Fader__fill}
              style={{ height: `${ratio * 100}%` }}
            />
          </div>
          <div
            className={styles.Fader__thumb}
            style={{ top: `${thumbTopPercent}%` }}
          />
        </div>
      </div>
      <span className={styles.Fader__label}>{label}</span>
      <span className={styles.Fader__value}>
        {formatValue(value)}
      </span>
    </div>
  )
}
